import type {
  DeclareExperiencesRequest,
  SaveComponentValueResponse,
  SubmitQuestionnaireRequest,
  SubmitQuizAttemptRequest,
} from "@repo/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  applyPublicProductAssessmentPreviousAnswer,
  declarePublicExperiences,
  getPublicProductAssessmentAssessments,
  getPublicProductAssessmentConditionValues,
  getPublicProductAssessmentPreviousAnswer,
  getPublicProductAssessmentValue,
  getPublicProductTypesInUse,
  startPublicQuiz,
  submitPublicQuestionnaire,
  submitPublicQuiz,
} from "@/lib/api/public-flow/product-assessment";
import { useSessionToken } from "@/lib/context/public-session-context";
import { invalidatePublicSuitabilityQueries } from "./use-goals";
import { useInvalidatePublicDynamicSync } from "./use-portfolios";
import { publicFlowCompletionQueryKey } from "./use-public-flow-completion";

export const keys = {
  value: ["publicFlow", "product-assessment", "flow-instance-value"] as const,
  previousAnswer: ["publicFlow", "product-assessment", "previous-answer"] as const,
  assessments: ["publicFlow", "product-assessment", "assessments"] as const,
  productTypesInUse: ["publicFlow", "product-assessment", "product-types-in-use"] as const,
  conditionValues: ["publicFlow", "product-assessment", "condition-values"] as const,
};

/**
 * QL-698 — the `__pa_…` condition values, resolved on the server from the full
 * record, so a member's session reads the whole group's result. Every public
 * product-assessment write invalidates it. Another member's writes show up only
 * after this session's own next write or a reload (window-focus refetch is off
 * app-wide) — Submit is checked server-side anyway.
 */
export function usePublicProductAssessmentConditionValues() {
  const token = useSessionToken();
  const { data } = useQuery({
    queryKey: keys.conditionValues,
    queryFn: () => getPublicProductAssessmentConditionValues(token),
    enabled: !!token,
  });
  return data?.values ?? null;
}

export function usePublicProductTypesInUse() {
  const token = useSessionToken();
  const { data } = useQuery({
    queryKey: keys.productTypesInUse,
    queryFn: () => getPublicProductTypesInUse(token),
    enabled: !!token,
  });
  return data?.productTypeIds ?? null;
}

export function usePublicProductAssessmentValue() {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: keys.value,
    queryFn: () => getPublicProductAssessmentValue(token),
    enabled: !!token,
  });
  return {
    inputData: data?.inputData ?? null,
    calculatedData: data?.calculatedData ?? null,
    error,
    isLoading,
  };
}

export function usePublicProductAssessmentPreviousAnswer() {
  const token = useSessionToken();
  const { data, isLoading } = useQuery({
    queryKey: keys.previousAnswer,
    queryFn: () => getPublicProductAssessmentPreviousAnswer(token),
    enabled: !!token,
  });
  return { previousAnswer: data ?? null, isLoading };
}

/**
 * Shared cache bookkeeping for every public product-assessment write that
 * returns the component save response (declare, questionnaire submit).
 */
function usePublicAssessmentWriteSuccess() {
  const queryClient = useQueryClient();
  const invalidateDynamicSync = useInvalidatePublicDynamicSync();
  return (result: SaveComponentValueResponse) => {
    if (result.success && result.inputData !== undefined) {
      queryClient.setQueryData(keys.value, {
        inputData: result.inputData,
        calculatedData: result.calculatedData,
      });
    }
    queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
    invalidatePublicSuitabilityQueries(queryClient);
    // QP-185 — the save regenerated dynamic portfolios.
    invalidateDynamicSync(result.dynamicSync);
    // QL-698 — returned so the mutation stays pending until the `__pa_…`
    // condition values are fresh; page visibility depends on them.
    return queryClient.invalidateQueries({ queryKey: keys.conditionValues });
  };
}

/** QL-194 — plain per-category declaration for declaration-only types. */
export function useDeclarePublicExperiences() {
  const token = useSessionToken();
  const onSuccess = usePublicAssessmentWriteSuccess();
  return useMutation({
    mutationFn: (request: DeclareExperiencesRequest) => declarePublicExperiences(token, request),
    onSuccess,
  });
}

/** QL-194 — one category's questionnaire, submitted whole on "Done". */
export function useSubmitPublicQuestionnaire() {
  const token = useSessionToken();
  const onSuccess = usePublicAssessmentWriteSuccess();
  return useMutation({
    mutationFn: (request: SubmitQuestionnaireRequest) => submitPublicQuestionnaire(token, request),
    onSuccess,
  });
}

/**
 * Public counterpart of `useApplyPreviousAnswer` — the backend merges and recomputes, and the
 * returned value blob primes the same cache the declare / questionnaire mutations write, so the widget's value-sync
 * effect converges without a refetch.
 */
export function useApplyPublicPreviousAnswer() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => applyPublicProductAssessmentPreviousAnswer(token),
    onSuccess: (res) => {
      queryClient.setQueryData(keys.value, {
        inputData: res.value.inputData,
        calculatedData: res.value.calculatedData,
      });
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
      invalidatePublicSuitabilityQueries(queryClient);
      // QL-698 — keep the mutation pending until the condition values are fresh.
      return queryClient.invalidateQueries({ queryKey: keys.conditionValues });
    },
    onError: () => {
      // Refusal (e.g. the instance was completed in another tab) — refetch the previous-answer
      // probe so the reuse choice screen reflects server truth.
      queryClient.invalidateQueries({ queryKey: keys.previousAnswer });
    },
  });
}

export function usePublicProductAssessmentAssessments() {
  const token = useSessionToken();
  const { data, error, isLoading } = useQuery({
    queryKey: keys.assessments,
    queryFn: () => getPublicProductAssessmentAssessments(token),
    enabled: !!token,
  });
  return { assessments: data ?? null, error, isLoading };
}

/** QL-505 — `subjectId` addresses one party when several answer (a group member, or a representative when 2+ are registered). */
export function useStartPublicQuizAttempt() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productTypeId, subjectId }: { productTypeId: string; subjectId?: string }) =>
      startPublicQuiz(token, productTypeId, subjectId),
    // QL-698 — starting an attempt writes the record, so the condition values may change.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.conditionValues }),
  });
}

export function useSubmitPublicQuizAttempt() {
  const token = useSessionToken();
  const queryClient = useQueryClient();
  const invalidateDynamicSync = useInvalidatePublicDynamicSync();
  return useMutation({
    mutationFn: (request: SubmitQuizAttemptRequest) => submitPublicQuiz(token, request),
    onSuccess: (result) => {
      // The submit response has no value blob — refetch it so quizStates /
      // calculatedData outcomes converge, and refresh completion like the
      // declare mutation does.
      queryClient.invalidateQueries({ queryKey: keys.value });
      queryClient.invalidateQueries({ queryKey: keys.conditionValues });
      queryClient.invalidateQueries({ queryKey: publicFlowCompletionQueryKey });
      invalidatePublicSuitabilityQueries(queryClient);
      // QP-185 — the quiz outcome regenerated dynamic portfolios.
      invalidateDynamicSync(result.dynamicSync);
    },
  });
}
